<template>
    <AdminLayout>
        <div class="ph">
            <div class="ptitle">Verification <span>Queue Center</span></div>
        </div>
        <div class="view" id="view-ops">
            <LoadingSpinner :isLoading="isLoading" />
            <div class="page-wrap">
                <div class="ops-banner">⚠ Scoring weights and point values are not shown at this access level. Verify
                    each
                    signal accurately — completeness determines the Care Worker's tier.</div>


                <!-- VERIFICATION PANEL -->
                <div id="ops-verify-view">
                    <button @click="backToQueue()"
                        style="font-size:12px;color:var(--muted);background:none;border:none;cursor:pointer;margin-bottom:16px;display:flex;align-items:center;gap:6px;">←
                        Back to queue</button>
                    <div class="ops-profile" id="ops-profile-card">
                        <div class="ops-av">
                            <img v-if="cg.profile_photo" :src="cg.profile_photo" />
                            <div v-else>
                                {{ initials }}
                            </div>
                        </div>
                        <div>
                            <div class="ops-profile-name" id="ops-pname">{{ cg.fn }} {{ cg.ln }}</div>
                            <div class="ops-profile-meta" id="ops-pmeta">{{ cg.cats?.join(' · ') }} · {{ cg.state }}
                            </div>
                            <div class="ops-profile-ref" id="ops-pref">{{ cg.care_id }}</div>
                        </div>
                        <div style="margin-left:auto;" id="ops-tier-badge"></div>
                    </div>


                    <!-- A: IDENTITY SIGNALS -->
                    <div class="sig-block">
                        <div class="sig-block-title">A · Identity Signals <span class="api-badge auto">API
                                Automated</span>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">BVN Verified <span class="api-badge auto">Prembly API</span></div>
                                <div class="sig-sub">Submitted: <span id="bvn-submitted" class="sig-doc">{{ maskedBVN
                                }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" v-model="bvnStatus">
                                    <option value="pending">— API Pending —</option>
                                    <option value="verified">✓ API Verified</option>
                                    <option value="failed">✕ API Failed / Not found</option>
                                </select>
                            </div>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">NIN Verified <span class="api-badge auto">NIMC API</span></div>
                                <div class="sig-sub">Submitted: <span class="sig-doc" id="nin-submitted">{{ maskedNIN
                                        }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" v-model="ninStatus">
                                    <option value="pending">— API Pending —</option>
                                    <option value="verified">✓ API Verified</option>
                                    <option value="failed">✕ API Failed / Not found</option>
                                </select>
                            </div>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Government ID <span class="api-badge manual">Manual + API</span>
                                </div>
                                <div class="sig-sub"><a class="sig-doc" :href="cg.documents?.id?.url"
                                        target="_blank">View uploaded document</a></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" v-model="cg.documents.id.status">
                                    <option value="pending">— Pending —</option>
                                    <option value="verified">✓ API Verified — Clear</option>
                                    <option value="verified">✓ Manual — Sighted & Clear</option>
                                    <option value="rejected">✕ Rejected</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- B: SAFETY & ACCOUNTABILITY -->
                    <div class="sig-block">
                        <div class="sig-block-title">B · Safety & Accountability</div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Criminal Background Check <span class="api-badge manual">Manual —
                                        NPF</span></div>

                                <div class="sig-sub">Upload report from Nigeria Police Force</div>
                            </div>
                            <div class="sig-right">

                                <select class="sig-select" id="v-criminal" v-model="cg.background.status">
                                    <option value="pending">— Pending —</option>

                                    <option value="clear">
                                        ✓ Clear — No record found
                                    </option>

                                    <option value="flagged">
                                        ✕ CRIMINAL RECORD FOUND
                                    </option>
                                </select>

                                <div class="npf-upload" v-if="
                                    cg.background.status === 'clear' ||
                                    cg.background.status === 'flagged'
                                ">
                                    <div class="doc-upload-zone" @click="$refs.npfReport.click()" style="padding:12px;">
                                        <div class="doc-upload-label">
                                            Upload NPF Report
                                        </div>

                                        <div class="doc-upload-sub">
                                            Scanned or photographed report
                                        </div>

                                        <input ref="npfReport" type="file" accept="image/*,.pdf" style="display:none"
                                            @change="handleDocUpload($event, 'npf_report')" />
                                    </div>

                                    <!-- UPLOADED FILE CARD -->
                                    <div v-if="docs.npf_report.file" class="doc-uploaded-card">

                                        <!-- Preview -->
                                        <div class="doc-thumb">

                                            <!-- Image Preview -->
                                            <template v-if="docs.npf_report.preview">
                                                <img :src="docs.npf_report.preview" alt="Document Preview" />
                                            </template>

                                            <!-- PDF Preview -->
                                            <template v-else>
                                                <div class="pdf-thumb">
                                                    📄
                                                </div>
                                            </template>

                                        </div>

                                        <!-- File Info -->
                                        <div class="doc-meta">

                                            <div class="doc-label">
                                                Uploaded NPF Report
                                            </div>

                                            <div class="doc-name">
                                                {{ docs.npf_report.file.name }}
                                            </div>

                                        </div>

                                        <!-- Remove -->
                                        <button type="button" class="doc-remove-btn" @click="removeDoc('npf_report')">
                                            Remove
                                        </button>

                                    </div>

                                    <!-- SUCCESS -->
                                    <div v-if="docs.npf_report.file"
                                        style="margin-top:10px;color:green;font-size:13px;">
                                        ✅ File selected successfully
                                    </div>

                                    <!-- ERROR -->
                                    <div v-if="errors.npf_report" class="field-error">
                                        {{ errors.npf_report }}
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Guarantor Verified <span class="api-badge manual">Manual —
                                        Phone/In-Person</span></div>
                                <div class="sig-sub">Call guarantor to confirm: <span class="sig-doc" id="gname-show">{{
                                    cg.gname }}</span> · <span id="gphone-show"
                                        style="color:var(--dkgreen);font-size:10px;font-weight:600;">{{ cg.gphone
                                        }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" id="v-guarantor" v-model="cg.gstatus">
                                    <option value="pending">— Pending —</option>
                                    <option value="verified">✓ Confirmed — Guarantor verified</option>
                                    <option value="unverified">✕ Not reachable / Unverified</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- C: CREDENTIALS & EXPERIENCE -->
                    <div class="sig-block">
                        <div class="sig-block-title">C · Credentials & Experience</div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Education Level</div>
                                <div class="sig-sub">Submitted: <span id="edu-submitted"
                                        style="color:var(--green);font-weight:500;">{{
                                            cg.education || 'Not provided'
                                        }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" id="v-edu" v-model="cg.education">

                                    <option value="">— Select —</option>
                                    <option value="No formal education">No formal education</option>
                                    <option value="Primary / FSLC">Primary / FSLC</option>
                                    <option value="Junior WAEC">Junior WAEC</option>
                                    <option value="WAEC / NECO (O'Level)">WAEC / NECO</option>
                                    <option value="OND / NCE">OND / NCE</option>
                                    <option value="HND / B.Sc and above">HND / B.Sc+</option>
                                </select>
                            </div>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Certificate Verification</div>
                                <div class="sig-sub"><a class="sig-doc" :href="cg.documents?.certificate?.url"
                                        target="_blank">View uploaded
                                        certificate</a>
                                </div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" id="v-cert" v-model="cg.documents.certificate.status">
                                    <option value="pending">— Pending —</option>
                                    <option value="verified">✓ Verified — Authentic</option>
                                    <option value="rejected">⚠ Not Verified — Cannot confirm</option>
                                    <option value="forged">🚫 Forged / Fake — Certificate invalid</option>
                                </select>
                            </div>
                        </div>

                        <!-- Employment History — up to 5 entries -->
                        <div class="sig-row" style="flex-direction:column;align-items:flex-start;gap:10px;">
                            <div style="display:flex;justify-content:space-between;align-items:center;width:100%;">
                                <div class="sig-name">Employment History (up to 5)</div>
                                <div style="display:flex;align-items:center;gap:10px;">
                                    <span style="font-size:11px;color:var(--muted);">Total verified:</span>
                                    <select class="sig-select" id="v-emp" style="min-width:160px;"
                                        v-model="cg.employment_verified_count">
                                        <option :value="0">0 — No verified employer</option>
                                        <option :value="1">1 employer verified</option>
                                        <option :value="2">2 employers verified</option>
                                        <option :value="3">3 employers verified</option>
                                        <option :value="4">4 employers verified</option>
                                        <option :value="5">5 employers verified</option>
                                    </select>
                                </div>
                            </div>
                            <div id="emp-history-ops">
                                <div v-for="(emp, i) in cg.employment" :key="i" class="emp-history-row">
                                    <div class="emp-row-header">
                                        <div>Employment #{{ i + 1 }}</div>

                                        <span v-if="emp.verified">✓ Verified</span>
                                        <span v-else>Pending</span>
                                    </div>

                                    <div class="form-grid">
                                        <div>{{ emp.name }}</div>
                                        <div>{{ emp.phone }}</div>
                                    </div>

                                    <div class="form-grid">
                                        <div>{{ emp.address }}</div>
                                        <div>{{ emp.email }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Care Type Coverage</div>
                                <div class="sig-sub">Submitted: <span id="cats-submitted"
                                        style="color:var(--green);font-weight:500;">{{
                                            cg.cats?.join(', ') || 'None'
                                        }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" id="v-caretype" v-model="careTypeCount">
                                    <option value="0">None confirmed</option>
                                    <option value="1">1 category</option>
                                    <option value="2">2 categories</option>
                                    <option value="3">3+ categories</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- D: REPUTATION -->
                    <div class="sig-block">
                        <div class="sig-block-title">D · Reputation</div>
                        <div class="rep-grid">
                            <div class="rep-counter">
                                <div class="rep-counter-label">Pre-Deployment Reviews</div>
                                <div class="rep-ctrl">
                                    <button class="rev-btn" @click="cg.reputation.pre--">−</button>
                                    <span class="rev-count" id="rep-pre">{{ cg.reputation?.pre }}</span>
                                    <button class="rev-btn" @click="cg.reputation.pre++">+</button>
                                </div>
                            </div>
                            <div class="rep-counter">
                                <div class="rep-counter-label">Post-Deployment Good Reviews</div>
                                <div class="rep-ctrl">
                                    <button class="rev-btn" @click="cg.reputation.good--">−</button>
                                    <span class="rev-count" id="rep-good">{{ cg.reputation?.good }}</span>
                                    <button class="rev-btn" @click="cg.reputation.good++">+</button>
                                </div>
                            </div>
                            <div class="rep-counter">
                                <div class="rep-counter-label">Post-Deployment Bad Reviews</div>
                                <div class="rep-ctrl">
                                    <button class="rev-btn" @click="cg.reputation.bad--"
                                        style="color:var(--red);">−</button>
                                    <span class="rev-count" id="rep-bad" style="color:var(--red);">{{ cg.reputation?.bad
                                        }}</span>
                                    <button class="rev-btn" @click="cg.reputation.bad++"
                                        style="color:var(--red);">+</button>
                                </div>
                            </div>
                        </div>

                        <div style="margin-top:14px;border-top:1px solid var(--border);padding-top:14px;">
                            <div class="sig-name" style="margin-bottom:8px;">Malpractice Escalations</div>
                            <select class="sig-select" id="v-malp" style="width:100%;" v-model="cg.malpractice.count">
                                <option :value="0">None — clean record</option>
                                <option :value="1">1 Case</option>
                                <!-- <option :value="1">1 unresolved escalation</option>
                            <option :value="-300">2 escalations (any type)</option>
                            <option :value="-500">3+ escalations — severe</option> -->
                            </select>
                        </div>
                    </div>

                    <button class="compute-btn" @click="computeScore()">▶ Compute Trust Score & Assign Tier</button>

                    <!-- DNE Banner -->
                    <div class="dne-banner" id="dne-banner" v-if="score_result?.do_not_employ">
                        <div class="dne-title">🚫 DO NOT EMPLOY</div>
                        <div class="dne-sub">This Care Giver has a confirmed criminal record. Per Succour policy, a Care
                            ID
                            cannot be issued and this profile cannot be deployed. The application should be formally
                            rejected and the Care Giver notified.</div>
                    </div>

                    <!-- Score Result -->
                    <div class="score-result" id="score-result" v-if="score_result">
                        <div class="sr-top">
                            <div>
                                <div class="sr-num" id="sr-num">{{ score_result.final_score }}</div>
                                <div class="sr-denom">/ 850</div>
                            </div>
                            <div class="sr-right">

                                <div class="sr-tier" :class="cg.verification.tier" id="sr-tier">
                                    {{ cg.verification.tier }}
                                </div>

                                <div class="sr-deploy" id="sr-deploy">
                                    {{
                                        cg.verification.do_not_employ
                                            ? "✕ Not deployment-ready"
                                    : "✓ Deployment-ready - Card ID can be issued"
                                    }}
                                </div>

                            </div>
                        </div>
                        <div class="sr-checklist" id="sr-checklist">

                            <!-- BVN -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.bvn > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.bvn > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">BVN Verified (API)</div>
                            </div>

                            <!-- NIN -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.nin > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.nin > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">NIN Verified (API)</div>
                            </div>

                            <!-- Government ID -->
                            <div class="sr-check">
                                <div class="sr-ck-icon"
                                    :class="cg.verification.scores.government_id > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.government_id > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Government ID sighted</div>
                            </div>

                            <!-- Criminal Check -->
                            <div class="sr-check">
                                <div class="sr-ck-icon"
                                    :class="cg.verification.scores.criminal_clearance > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.criminal_clearance > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Criminal check — clear</div>
                            </div>

                            <!-- Guarantor -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.guarantor > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.guarantor > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Guarantor verified</div>
                            </div>

                            <!-- Education -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.education > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.education > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Education on record</div>
                            </div>

                            <!-- Employment -->
                            <div class="sr-check">
                                <div class="sr-ck-icon"
                                    :class="cg.verification.scores.employment_score > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.employment_score > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Employment history verified</div>
                            </div>

                            <!-- Reviews -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.reputation > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.reputation > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">At least 1 good review</div>
                            </div>

                            <!-- Warning -->
                            <div class="sr-check warn" v-if="
                                cg.verification.scores.bvn <= 0 &&
                                cg.verification.scores.nin <= 0
                            ">
                                <div class="sr-ck-icon warn">!</div>

                                <div class="sr-ck-lbl warn-text">
                                    Neither BVN nor NIN verified — capped at Bronze
                                </div>
                            </div>

                            <!-- Gold Lock -->
                            <div class="sr-check" v-if="cg.verification.scores.gold_gate_failed">
                                <div class="sr-ck-icon" :class="cg.verification.tier === 'gold' ? 'ok' : 'fail'">
                                    {{ cg.verification.tier === 'gold' ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">
                                    Gold not unlocked — requires identity + safety + credentials + ≥1 good review
                                </div>
                            </div>

                        </div>
                        <button class="issue-btn" id="issue-btn" @click="issueCareID()"
                            v-if="!cg.verification.do_not_employ">✓ Issue
                            Care ID &amp; Notify
                            Care Giver</button>
                    </div>

                </div>

            </div>
        </div>
    </AdminLayout>

</template>

<script lang="js">
import AdminLayout from '../../Layouts/AdminLayout.vue';
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Request from "@/utils/apiclient.js";

export default {
    components: {
        AdminLayout,
        LoadingSpinner,

    },
    data() {
        return {
            isLoading: false,
            docs: {

                npf_report: {
                    file: null,
                    type: null,
                    preview: null
                }

            },

            errors: {},
            cg: {

                id: null,
                care_id: null,
                status: 'pending',

                profile_photo: null,

                fn: '',
                ln: '',

                dob: null,
                gender: '',

                phone: '',
                whatsapp: '',
                email: '',

                state: '',

                exp_type: '',
                exp_years: '',

                arrangement: '',
                availability: '',

                education: '',
                english: '',

                cats: [],

                // -----------------------------
                // IDENTITY
                // -----------------------------
                bvn: '',
                nin: '',

                bvn_verified: null,
                nin_verified: null,

                // -----------------------------
                // GUARANTOR
                // -----------------------------
                gname: '',
                gphone: '',
                grel: '',
                gocc: '',
                gaddress: '',
                gstatus: 'pending',

                // -----------------------------
                // DOCUMENTS
                // -----------------------------
                documents: {

                    id: {
                        url: null,
                        status: 'pending'
                    },

                    certificate: {
                        url: null,
                        status: 'pending'
                    },

                    guarantor: {
                        url: null,
                        status: 'pending'
                    },
                    employment: {
                        url: null,
                        status: 'pending'
                    },

                },

                // -----------------------------
                // EMPLOYMENT
                // -----------------------------
                employment: [],

                employment_verified_count: 0,

                // -----------------------------
                // BACKGROUND CHECK
                // -----------------------------
                background: {

                    provider: null,

                    status: 'pending',

                    criminal_record: false,

                    notes: ''
                },

                // -----------------------------
                // REPUTATION
                // -----------------------------
                reputation: {

                    pre: 0,

                    good: 0,

                    bad: 0,

                    warnings: 0,

                    suspensions: 0
                },

                // -----------------------------
                // MALPRACTICE
                // -----------------------------
                malpractice: {

                    count: 0,

                    penalty_total: 0,

                    do_not_employ: false
                },

                // -----------------------------
                // VERIFICATION
                // -----------------------------


                verification: {
                    "raw_score": 0,
                    "trust_score": 0,
                    "tier": "unverified",
                    "do_not_employ": false,
                    "scores": {
                        "bvn": 0,
                        "nin": 0,
                        "government_id": 0,
                        "criminal_clearance": 0,
                        "guarantor": 0,
                        "education": 0,
                        "certificate_score": 0,
                        "employment_score": 0,
                        "care_categories": 0,
                        "reputation_score": 0,
                        "malpractice_penalty": 0,
                        "bronze_cap_reason": "",
                        "silver_cap_reason": "",
                        "gold_gate_failed": false
                    }
                }


            },

            score_result: null,
        }
    },
    mounted() {
        this.fetchCargiverDetails();
    },

    computed: {
        initials() {
            return (this.cg?.fn?.[0] || '?') + (this.cg?.ln?.[0] || '?');
        },
        maskedBVN() {
            return this.cg?.bvn
                ? `BVN: •••••${this.cg.bvn.slice(-4)}`
                : 'Not provided';
        },

        maskedNIN() {
            return this.cg?.nin
                ? `NIN: •••••${this.cg.nin.slice(-4)}`
                : 'Not provided';
        },
        bvnStatus: {
            get() {
                if (this.cg?.bvn_verified === true) return 'verified';
                if (this.cg?.bvn_verified === false) return 'failed';
                return 'pending';
            },
            set(val) {
                this.cg.bvn_verified = (val === 'verified');
            }
        },
        ninStatus: {
            get() {
                if (this.cg?.nin_verified === true) return 'verified';
                if (this.cg?.nin_verified === false) return 'failed';
                return 'pending';
            },
            set(val) {
                this.cg.nin_verified = (val === 'verified');
            }
        },
        careTypeCount: {
            get() {
                return this.cg?.cats?.length || 0;
            },
            set(val) {
                console.log(val);
                // optional: enforce UI-only mapping or trigger logic
                // since cg.cats is an array, you usually DON'T directly set length
                console.warn("careTypeCount is derived from cg.cats. Update cg.cats instead.");
            }
        }
    },

    methods: {
        backToQueue() {
    window.history.back()
},
        formatKey(key) {
            return key
                .replace(/_/g, " ")
                .replace(/\b\w/g, l => l.toUpperCase());
        },

        triggerUpload(refName) {

            this.$refs[refName]?.click();
        },

        handleDocUpload(event, type) {

            const file = event.target.files[0];

            if (!file) return;

            // max 5MB
            const maxSize = 5 * 1024 * 1024;

            if (file.size > maxSize) {

                this.errors[type] = 'File must not exceed 5MB';

                // reset input
                event.target.value = null;

                return;
            }

            const allowed = [
                'image/jpeg',
                'image/png',
                'image/jpg',
                'application/pdf'
            ];

            if (!allowed.includes(file.type)) {

                this.errors[type] = 'Only JPG, PNG or PDF files allowed';

                // reset input
                event.target.value = null;

                return;
            }

            // clear old error
            delete this.errors[type];

            // ensure object exists
            if (!this.docs[type]) {

                this.docs[type] = {
                    file: null,
                    type: null,
                    preview: null
                };
            }

            // clear previous preview
            this.docs[type].preview = null;

            // store file
            this.docs[type].file = file;
            this.docs[type].type = file.type;

            // PDF → no preview
            if (file.type === 'application/pdf') {

                return;
            }

            // image preview
            const reader = new FileReader();

            reader.onload = (e) => {

                this.docs[type].preview = e.target.result;
            };

            reader.readAsDataURL(file);
        },

        removeDoc(type) {

            // clear vue state
            this.docs[type] = {
                file: null,
                preview: null,
                type: null,
            };

            // clear error
            delete this.errors[type];



            // reset native input
            const inputMap = {
                npf_report: 'npfReport',

            };

            const refName = inputMap[type];

            if (this.$refs[refName]) {
                this.$refs[refName].value = null;
            }
        },
        async fetchCargiverDetails() {
            try {

                this.isLoading = true;

                const request = new Request(
                    `/verification-center/${this.$route.params.id}/`
                );

                await request.get((res, error) => {

                    if (error) {
                        console.log("FETCH ERROR", error);
                        return;
                    }

                    console.log("ADMIN DATA", res);

                    if (res) {
                        this.cg = res;
                    }


                });

            } catch (err) {

                console.log("FETCH FAILED", err);

            } finally {

                this.isLoading = false;
            }
        },
        async saveVerificationReview() {

            try {

                this.isLoading = true;

                const formData = new FormData();

                // ---------------------------------
                // IDENTITY
                // ---------------------------------

                formData.append(
                    "bvn_verified",
                    this.cg.bvn_verified
                );

                formData.append(
                    "nin_verified",
                    this.cg.nin_verified
                );

                // ---------------------------------
                // DOCUMENTS
                // ---------------------------------

                formData.append(
                    "documents[id][status]",
                    this.cg.documents.id.status
                );

                formData.append(
                    "documents[certificate][status]",
                    this.cg.documents.certificate.status
                );

                // ---------------------------------
                // BACKGROUND
                // ---------------------------------

                formData.append(
                    "background_status",
                    this.cg.background.status
                );

                if (this.docs.npf_report.file) {

                    formData.append(
                        "npf_report",
                        this.docs.npf_report.file
                    );
                }

                // ---------------------------------
                // GUARANTOR
                // ---------------------------------

                formData.append(
                    "guarantor_verified",
                    this.cg.gstatus
                );

                // ---------------------------------
                // EMPLOYMENT
                // ---------------------------------

                formData.append(
                    "employment_verified_count",
                    this.cg.employment_verified_count
                );

                // ---------------------------------
                // REPUTATION
                // ---------------------------------

                formData.append(
                    "rep_pre",
                    this.cg.reputation.pre
                );

                formData.append(
                    "rep_good",
                    this.cg.reputation.good
                );

                formData.append(
                    "rep_bad",
                    this.cg.reputation.bad
                );

                // ---------------------------------
                // MALPRACTICE
                // ---------------------------------

                formData.append(
                    "malpractice_count",
                    this.cg.malpractice.count
                );

                console.log(formData)

                const request = new Request(
                    `/verification-center/${this.cg.id}/save/`
                );

                await request.patch(
                    formData,
                    (res, error) => {

                        if (error) {

                            console.log(error);

                            return;
                        }

                        console.log(res);
                    }
                );

            } catch (err) {

                console.log(err);

            } finally {

                this.isLoading = false;
            }
        },
        async computeScore() {

            try {

                // save first
                await this.saveVerificationReview();

                this.isLoading = true;

                const request = new Request(
                    `/verification-center/${this.cg.id}/compute/`
                );

                await request.post(
                    {},
                    (res, error) => {

                        if (error) {

                            console.log(error);

                            return;
                        }

                        console.log("TRUST SCORE", res);
                        this.score_result = res;

                        this.cg.verification = {

                            raw_score: res.raw_score,

                            trust_score: res.final_score,

                            tier: res.tier,

                            do_not_employ: res.do_not_employ,

                            scores: res.breakdown,
                        };

                        console.log(this.verification)


                    }
                );

            } catch (err) {

                console.log(err);

            } finally {

                this.isLoading = false;
            }
        },
        async issueCareID() {
            try {
                if (!this.cg || !this.score_result) return;

                this.isLoading = true;

                const request = new Request(
                    `/verification-center/${this.cg.id}/issue-care-id/`
                );

                await request.post(
                    {
                        care_id: this.cg.care_id,
                    },
                    (res, error) => {
                        if (error) {
                            console.log("ISSUE CARE ID ERROR", error);
                            return;
                        }

                        console.log("CARE ID ISSUED", res);

                        // update local state if backend returns updated caregiver
                        if (res?.cg) {
                            this.cg = res.cg;
                        }

                        if (res?.care_id) {
                            this.cg.care_id = res.care_id;
                        }

                        if (res?.status) {
                            this.cg.status = res.status;
                        }

                        // optional: if backend confirms tier update
                        if (res?.tier) {
                            this.cg.verification.tier = res.tier;
                        }

                        this.$router.push({
                name: 'careid-view',
                params: {
                    id: this.cg.id
                }
            });
                    }
                );

            } catch (err) {
                console.log("ISSUE CARE ID FAILED", err);

            } finally {
                this.isLoading = false;
            }
        }
    }
}

</script>

<style>
:root {
    --green: #17865F;
    --dkgreen: #0D5C40;
    --mdgreen: #2A9D74;
    --ltgreen: #E8F4EF;
    --bg: #D6EDE2;
    --card: #EEF7F2;
    --pink: #F59CAB;
    --amber: #FF9700;
    --white: #FFFFFF;
    --text: #1A1A1A;
    --muted: #5A7A69;
    --border: #C0DDD0;
    --red: #E74C3C;
    --red-lt: #FEE2E2;
    --orange: #F97316;
    --blue: #2563EB;
    --blue-lt: #EFF6FF;
}
</style>
<style lang="css" scoped>
* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

body {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
}

/* ── SYSTEM NAV ── */
.sys-nav {
    background: var(--dkgreen);
    display: flex;
    align-items: stretch;
    border-bottom: 2px solid rgba(0, 0, 0, 0.15);
    position: sticky;
    top: 0;
    z-index: 200;
    overflow-x: auto;
}

.sys-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
}

.sys-logo-mark {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: var(--pink);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 13px;
    color: white;
    font-weight: 700;
}

.sys-logo-text {
    font-size: 9px;
    letter-spacing: 3px;
    color: rgba(255, 255, 255, 0.65);
    font-weight: 600;
}

.sys-logo-tm {
    font-size: 6px;
    color: rgba(255, 255, 255, 0.35);
    vertical-align: super;
}

.sys-steps {
    display: flex;
    flex: 1;
    min-width: 0;
}

.sys-step {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    cursor: pointer;
    border-bottom: 3px solid transparent;
    transition: 0.2s;
    position: relative;
    flex-shrink: 0;
}

.sys-step:hover {
    background: rgba(255, 255, 255, 0.05);
}

.sys-step.active {
    border-bottom-color: var(--pink);
}

.sys-step.active .step-num {
    background: var(--pink);
    color: white;
}

.sys-step.active .step-label {
    color: white;
}

.sys-step.done .step-num {
    background: rgba(39, 174, 96, 0.3);
    color: #7FD4A6;
}

.sys-step.done .step-label {
    color: rgba(255, 255, 255, 0.6);
}

.step-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.4);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: 0.2s;
}

.step-label {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.4);
    transition: 0.2s;
    white-space: nowrap;
}

.step-arrow {
    color: rgba(255, 255, 255, 0.15);
    font-size: 14px;
    flex-shrink: 0;
}

.sys-badge {
    margin: auto 16px auto auto;
    font-size: 9px;
    letter-spacing: 2px;
    color: rgba(255, 255, 255, 0.3);
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
}

/* ── VIEWS ── */
.view {
    display: block;
}

.view.active {
    display: block;
}

/* ── SHARED ── */
.page-wrap {
    max-width: 100%;
    margin: 0 auto;
    /* padding: 36px 24px 80px; */
}

.ph {
    margin-bottom: 14px;
}

.ptitle {
    font-size: 18px;
    font-weight: 600;
    color: #1A2E22;
}

.ptitle span {
    color: #17865F;
}

.page-hero {
    background: var(--dkgreen);
    padding: 36px 40px 44px;
    text-align: center;
}

.hero-tag {
    display: inline-block;
    font-size: 9px;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--pink);
    border: 1px solid rgba(245, 156, 171, 0.3);
    border-radius: 20px;
    padding: 4px 14px;
    margin-bottom: 12px;
}

.hero-title {
    font-family: 'Playfair Display', serif;
    font-size: 30px;
    color: white;
    font-weight: 700;
    margin-bottom: 8px;
    line-height: 1.25;
}

.hero-title em {
    color: var(--pink);
    font-style: italic;
}

.hero-sub {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    max-width: 480px;
    margin: 0 auto;
    line-height: 1.6;
}

/* ── PROGRESS BAR ── */
.progress-bar {
    display: flex;
    gap: 0;
    margin-bottom: 28px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid var(--border);
}

.pb-step {
    flex: 1;
    padding: 10px 14px;
    background: var(--card);
    border-right: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 8px;
    transition: 0.3s;
}

.pb-step:last-child {
    border-right: none;
}

.pb-step.active {
    background: var(--green);
}

.pb-step.done {
    background: var(--dkgreen);
}

.pb-num {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.5);
    font-size: 9px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.pb-step.active .pb-num,
.pb-step.done .pb-num {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.pb-lbl {
    font-size: 10px;
    color: var(--muted);
}

.pb-step.active .pb-lbl,
.pb-step.done .pb-lbl {
    color: white;
}

/* ── SECTION CARDS ── */
.section-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 24px 26px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.section-icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: var(--green);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
}

/* ── FORM FIELDS ── */
.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.form-grid-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.form-grid-1 {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.field label {
    font-size: 10px;
    font-weight: 500;
    color: var(--muted);
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.field label .req {
    color: var(--red);
    margin-left: 2px;
}

.field input,
.field select,
.field textarea {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 9px;
    padding: 10px 13px;
    color: var(--text);
    outline: none;
    transition: 0.2s;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
    border-color: var(--green);
    box-shadow: 0 0 0 3px rgba(23, 134, 95, 0.1);
}

.field input.invalid {
    border-color: var(--red);
    background: #FFF5F5;
}

.field textarea {
    resize: vertical;
    min-height: 80px;
}

.field select option {
    background: white;
}

.field-hint {
    font-size: 10px;
    color: var(--muted);
    margin-top: 3px;
}

/* ── PHOTO UPLOAD ── */
.photo-upload {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 14px;
}

.photo-preview {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: var(--ltgreen);
    border: 2px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    flex-shrink: 0;
    overflow: hidden;
    cursor: pointer;
    transition: 0.2s;
}

.photo-preview:hover {
    border-color: var(--green);
}

.photo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.photo-upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
}

.photo-upload-btn:hover {
    border-color: var(--green);
    background: var(--ltgreen);
}

.photo-upload-hint {
    font-size: 10px;
    color: var(--muted);
    margin-top: 5px;
}

.photo-required-note {
    font-size: 10px;
    color: var(--red);
    margin-top: 5px;
    display: none;
}

/* ── DOCUMENT UPLOAD ── */
.doc-upload-zone {
    border: 2px dashed var(--border);
    border-radius: 10px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: 0.2s;
    background: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.doc-upload-zone:hover,
.doc-upload-zone.drag {
    border-color: var(--green);
    background: var(--ltgreen);
}

.doc-icon {
    font-size: 24px;
}

.doc-upload-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
}

.doc-upload-sub {
    font-size: 10px;
    color: var(--muted);
}

.doc-upload-zone input[type=file] {
    display: none;
}

.doc-uploaded {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--ltgreen);
    border: 1px solid var(--border);
    border-radius: 8px;
    margin-top: 8px;
}

.doc-uploaded-name {
    font-size: 12px;
    color: var(--dkgreen);
    font-weight: 500;
    flex: 1;
}

.doc-remove {
    font-size: 10px;
    color: var(--red);
    cursor: pointer;
    font-weight: 600;
}

/* camera / upload dual option */
.dual-upload {
    display: flex;
    gap: 10px;
    margin-bottom: 8px;
}

.dual-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 14px 10px;
    border-radius: 10px;
    border: 1.5px dashed var(--border);
    background: white;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: var(--dkgreen);
    transition: 0.2s;
}

.dual-btn:hover {
    border-color: var(--green);
    background: var(--ltgreen);
}

.dual-btn span {
    font-size: 22px;
}

/* ── REFERENCE ROWS ── */
.ref-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 10px;
    align-items: end;
    margin-bottom: 10px;
    background: white;
    padding: 12px;
    border-radius: 9px;
    border: 1px solid var(--border);
}

.ref-row-num {
    font-size: 10px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 4px;
}

.ref-remove-btn {
    width: 30px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: white;
    color: var(--red);
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.2s;
    align-self: flex-end;
}

.ref-remove-btn:hover {
    background: var(--red-lt);
}

.add-ref-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px dashed var(--green);
    background: var(--ltgreen);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
    margin-top: 4px;
}

.add-ref-btn:hover {
    background: var(--card);
}

/* ── EMPLOYMENT HISTORY ROWS ── */
.emp-history-row {
    background: white;
    padding: 14px 16px;
    border-radius: 9px;
    border: 1px solid var(--border);
    margin-bottom: 10px;
}

.emp-row-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.emp-row-num {
    font-size: 10px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
}

.emp-row-remove {
    font-size: 11px;
    color: var(--red);
    cursor: pointer;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 5px;
    border: 1px solid var(--red);
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    transition: 0.2s;
}

.emp-row-remove:hover {
    background: var(--red-lt);
}

.add-emp-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px dashed var(--green);
    background: var(--ltgreen);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
    margin-top: 4px;
}

.add-emp-btn:hover {
    background: var(--card);
}

/* ── CATEGORY TOGGLES ── */
.cat-grid {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.cat-toggle {
    padding: 8px 16px;
    border-radius: 20px;
    border: 1px solid var(--border);
    background: white;
    font-size: 12px;
    font-weight: 500;
    color: var(--muted);
    cursor: pointer;
    transition: 0.2s;
    user-select: none;
}

.cat-toggle.selected {
    background: var(--green);
    border-color: var(--green);
    color: white;
}

/* ── CONSENT BOX ── */
.consent-box {
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    padding: 16px 18px;
    margin-bottom: 16px;
}

.consent-inner {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.consent-inner input[type=checkbox] {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    flex-shrink: 0;
    accent-color: var(--green);
    cursor: pointer;
}

.consent-text {
    font-size: 12px;
    color: var(--text);
    line-height: 1.6;
}

.consent-text strong {
    color: var(--dkgreen);
}

/* ── SUBMIT BTN ── */
.submit-btn {
    width: 100%;
    padding: 15px;
    border-radius: 10px;
    border: none;
    background: var(--green);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 8px;
}

.submit-btn:hover {
    background: var(--mdgreen);
}

.submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.doc-uploaded-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px;
    margin-top: 14px;
    border-radius: 16px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
    transition: 0.25s ease;
}

.doc-remove-btn {
    border: none;
    background: #ffe9e9;
    color: #d93025;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: 0.2s ease;
}

/* ── SUCCESS STATE ── */
.success-state {
    text-align: center;
    padding: 48px 24px;
    display: none;
}

.success-state.show {
    display: block;
    animation: fadeUp 0.4s ease;
}

.success-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.success-title {
    font-family: 'Playfair Display', serif;
    font-size: 24px;
    color: var(--dkgreen);
    margin-bottom: 8px;
}

.success-sub {
    font-size: 13px;
    color: var(--muted);
    max-width: 400px;
    margin: 0 auto 24px;
    line-height: 1.6;
}

.success-ref {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    color: var(--green);
    background: var(--ltgreen);
    padding: 8px 20px;
    border-radius: 8px;
    border: 1px solid var(--border);
    display: inline-block;
}

/* ── OPS QUEUE ── */
.ops-banner {
    background: #FFFBEB;
    border: 1px solid rgba(255, 151, 0, 0.3);
    border-radius: 10px;
    padding: 12px 18px;
    margin-bottom: 20px;
    font-size: 11px;
    color: #92400E;
    display: flex;
    align-items: center;
    gap: 8px;
}

.queue-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
}

.queue-table th {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    padding: 10px 12px;
    text-align: left;
    border-bottom: 2px solid var(--border);
}

.queue-table td {
    font-size: 12px;
    color: var(--text);
    padding: 12px 12px;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
}

.queue-table tr:hover td {
    background: var(--ltgreen);
}

.status-pill {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 10px;
    border-radius: 20px;
}

.status-pill.pending {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.25);
}

.status-pill.in-progress {
    background: rgba(37, 99, 235, 0.08);
    color: #1D4ED8;
    border: 1px solid rgba(37, 99, 235, 0.2);
}

.status-pill.complete {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.review-btn {
    font-size: 11px;
    font-weight: 600;
    padding: 5px 14px;
    border-radius: 7px;
    border: 1px solid var(--border);
    background: white;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
}

.review-btn:hover {
    background: var(--ltgreen);
    border-color: var(--green);
}

/* ── OPS VERIFICATION PANEL ── */
.ops-profile {
    background: white;
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 20px 22px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
}

.ops-av {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--green), #2CC89A);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    color: white;
    font-weight: 700;
    flex-shrink: 0;
    overflow: hidden;
}

.ops-av img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ops-profile-name {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    color: var(--dkgreen);
}

.ops-profile-meta {
    font-size: 11px;
    color: var(--muted);
    margin-top: 3px;
}

.ops-profile-ref {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: var(--muted);
    margin-top: 2px;
}

/* API badge */
.api-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 9px;
    border-radius: 20px;
    margin-left: 8px;
}

.api-badge.auto {
    background: rgba(37, 99, 235, 0.08);
    color: #1D4ED8;
    border: 1px solid rgba(37, 99, 235, 0.2);
}

.api-badge.manual {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.3);
}

/* signals */
.sig-block {
    background: white;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 14px;
}

.sig-block-title {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.sig-block-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.sig-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 0;
    border-bottom: 1px solid var(--border);
}

.sig-row:last-child {
    border: none;
    padding-bottom: 0;
}

.sig-left {
    flex: 1;
}

.sig-name {
    font-size: 12px;
    font-weight: 500;
    color: var(--text);
}

.sig-sub {
    font-size: 10px;
    color: var(--muted);
    margin-top: 2px;
}

.sig-doc {
    font-size: 10px;
    color: var(--green);
    text-decoration: underline;
    cursor: pointer;
    margin-top: 2px;
}

.sig-select {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 7px;
    padding: 6px 10px;
    color: var(--text);
    outline: none;
    min-width: 200px;
    cursor: pointer;
}

.sig-select:focus {
    border-color: var(--green);
}

.sig-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
}

/* reputation counters */
.rep-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-top: 8px;
}

.rep-counter {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 12px;
    text-align: center;
}

.rep-counter-label {
    font-size: 9px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
}

.rep-ctrl {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.rev-btn {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 1px solid var(--border);
    background: white;
    color: var(--green);
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.15s;
}

.rev-btn:hover {
    background: var(--ltgreen);
    border-color: var(--green);
}

.rev-count {
    font-size: 16px;
    font-weight: 700;
    color: var(--dkgreen);
    min-width: 22px;
    text-align: center;
}

/* NPF upload */
.npf-upload {
    margin-top: 10px;
    display: block;
}

.npf-upload.show {
    display: block;
}

/* compute */
.compute-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: var(--green);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 4px;
}

.compute-btn:hover {
    background: var(--mdgreen);
}

/* DNE banner */
.dne-banner {
    background: #FEE2E2;
    border: 2px solid var(--red);
    border-radius: 12px;
    padding: 20px 22px;
    margin-top: 14px;
    /* display: none; */
    animation: fadeUp 0.35s ease;
}

.dne-banner.show {
    display: block;
}

.dne-title {
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    color: var(--red);
    font-weight: 700;
    margin-bottom: 6px;
}

.dne-sub {
    font-size: 13px;
    color: #7F1D1D;
    line-height: 1.6;
}

/* score result */
.score-result {
    /* display: none; */
    background: var(--dkgreen);
    border-radius: 14px;
    padding: 24px 26px;
    margin-top: 14px;
}

.score-result.show {
    display: block;
    animation: fadeUp 0.35s ease;
}

.sr-top {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
}

.sr-num {
    font-family: 'Playfair Display', serif;
    font-size: 56px;
    color: white;
    line-height: 1;
    font-weight: 700;
}

.sr-denom {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.35);
    margin-top: 4px;
}

.sr-tier {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 16px;
    border-radius: 20px;
    display: inline-block;
    margin-bottom: 6px;
}

.sr-tier.unverified {
    background: rgba(231, 76, 60, 0.2);
    color: #FCA5A5;
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.sr-tier.bronze {
    background: rgba(255, 151, 0, 0.2);
    color: var(--amber);
    border: 1px solid rgba(255, 151, 0, 0.3);
}

.sr-tier.silver {
    background: rgba(200, 230, 215, 0.2);
    color: #A7D4BE;
    border: 1px solid rgba(200, 230, 215, 0.3);
}

.sr-tier.gold {
    background: rgba(255, 215, 0, 0.15);
    color: #FFD700;
    border: 1px solid rgba(255, 215, 0, 0.25);
}

.sr-deploy {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.45);
}

.sr-checklist {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.sr-check {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.05);
}

.sr-ck-icon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    flex-shrink: 0;
}

.sr-ck-icon.ok {
    background: rgba(39, 174, 96, 0.25);
    color: #7FD4A6;
}

.sr-ck-icon.fail {
    background: rgba(231, 76, 60, 0.2);
    color: #FCA5A5;
}

.sr-ck-lbl {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
}

.issue-btn {
    width: 100%;
    padding: 13px;
    border-radius: 10px;
    border: none;
    background: var(--pink);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 12px;
}

.issue-btn:hover {
    background: #e08a99;
}

/* ── EMPLOYER VIEW (standard) ── */
.ev-card {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    max-width: 480px;
    margin: 0 auto;
}

.ev-stripe {
    height: 5px;
    background: linear-gradient(90deg, var(--green), #2CC89A, var(--pink));
}

.ev-body {
    padding: 24px 26px;
}

.ev-hdr {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid #EEF4F1;
}

.ev-photo {
    width: 52px;
    height: 52px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--green), #2CC89A);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    color: white;
    font-weight: 700;
    flex-shrink: 0;
    overflow: hidden;
}

.ev-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ev-name {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    color: var(--text);
    font-weight: 700;
}

.ev-sc {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: var(--muted);
    margin-top: 3px;
}

.ev-tbadge {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 14px;
    border-radius: 20px;
}

.ev-tbadge.gold {
    background: rgba(255, 215, 0, 0.1);
    color: #B8860B;
    border: 1px solid rgba(255, 215, 0, 0.25);
}

.ev-tbadge.silver {
    background: var(--ltgreen);
    color: var(--dkgreen);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.ev-tbadge.bronze {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.2);
}

.ev-score-block {
    display: flex;
    align-items: center;
    gap: 14px;
    background: var(--ltgreen);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 18px;
    border: 1px solid rgba(23, 134, 95, 0.12);
}

.ev-score-num {
    font-family: 'Playfair Display', serif;
    font-size: 46px;
    color: var(--dkgreen);
    line-height: 1;
    font-weight: 700;
}

.ev-score-denom {
    font-size: 12px;
    color: var(--muted);
}

.ev-score-level {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 12px;
    border-radius: 20px;
    margin-top: 4px;
}

.ev-score-level.gold {
    background: rgba(255, 215, 0, 0.1);
    color: #B8860B;
    border: 1px solid rgba(255, 215, 0, 0.2);
}

.ev-score-level.silver {
    background: rgba(23, 134, 95, 0.1);
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.ev-score-level.bronze {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.15);
}

.ev-cats-lbl {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 7px;
}

.ev-cats {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.ev-cat {
    font-size: 10px;
    color: var(--dkgreen);
    font-weight: 500;
    background: var(--ltgreen);
    border: 1px solid var(--border);
    padding: 4px 10px;
    border-radius: 20px;
}

.ev-br-lbl {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 10px;
}

.ev-row-item {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.ev-rl {
    font-size: 11px;
    color: var(--text);
    width: 100px;
    flex-shrink: 0;
}

.ev-bar-track {
    flex: 1;
    height: 6px;
    background: #EEF4F1;
    border-radius: 6px;
    overflow: hidden;
}

.ev-bar-fill {
    height: 100%;
    border-radius: 6px;
}

.ev-bar-fill.excellent {
    background: var(--green);
    width: 92%;
}

.ev-bar-fill.good {
    background: var(--amber);
    width: 70%;
}

.ev-bar-fill.average {
    background: #F0A05A;
    width: 48%;
}

.ev-bar-fill.poor {
    background: var(--red);
    width: 24%;
}

.ev-rval {
    font-size: 10px;
    font-weight: 600;
    width: 58px;
    text-align: right;
}

.ev-rval.excellent {
    color: var(--green);
}

.ev-rval.good {
    color: #B36800;
}

.ev-rval.average {
    color: #E07020;
}

.ev-rval.poor {
    color: var(--red);
}

.ev-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 14px;
    background: #E8F7F0;
    border-radius: 10px;
    border: 1px solid rgba(23, 134, 95, 0.2);
    margin: 14px 0;
}

.ev-sdot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--green);
    flex-shrink: 0;
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1
    }

    50% {
        opacity: 0.5
    }
}

.ev-stext {
    font-size: 12px;
    color: var(--dkgreen);
    font-weight: 600;
}

.ev-sdate {
    font-size: 10px;
    color: var(--muted);
    margin-left: auto;
}

.ev-qr-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 14px;
    border-top: 1px solid #EEF4F1;
}

.ev-qr-box {
    width: 60px;
    height: 60px;
    background: var(--ltgreen);
    border-radius: 8px;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ev-share-btns {
    display: flex;
    gap: 8px;
}

.ev-share-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    transition: 0.2s;
}

.ev-share-btn.wa {
    background: #25D366;
    color: white;
}

.ev-share-btn.wa:hover {
    background: #1DA851;
}

.ev-share-btn.copy {
    background: var(--ltgreen);
    color: var(--dkgreen);
    border: 1px solid var(--border);
}

.ev-share-btn.copy:hover {
    background: #D0EBE0;
}

.ev-footer-lnk {
    font-size: 9px;
    color: var(--muted);
    text-align: center;
    margin-top: 12px;
}

.ev-footer-lnk a {
    color: var(--green);
    font-weight: 600;
    text-decoration: none;
}

/* ── DETAILED EMPLOYER VIEW (payment gated) ── */
.pay-gate {
    background: var(--dkgreen);
    border-radius: 16px;
    overflow: hidden;
    max-width: 560px;
    margin: 0 auto 20px;
}

.pay-gate-body {
    padding: 32px;
}

.pay-gate-icon {
    font-size: 36px;
    margin-bottom: 12px;
}

.pay-gate-title {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    color: white;
    margin-bottom: 8px;
}

.pay-gate-sub {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.55);
    line-height: 1.6;
    margin-bottom: 24px;
}

.pay-features {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
}

.pay-feature {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
}

.pay-feature-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--pink);
    flex-shrink: 0;
}

.pay-price {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 20px;
}

.pay-amount {
    font-family: 'Playfair Display', serif;
    font-size: 32px;
    color: white;
    font-weight: 700;
}

.pay-period {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.45);
}

.pay-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: var(--pink);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
}

.pay-btn:hover {
    background: #e08a99;
}

.pay-note {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.3);
    text-align: center;
    margin-top: 10px;
}

/* detailed report */
.det-report {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    max-width: 600px;
    margin: 0 auto;
    display: none;
    animation: fadeUp 0.4s ease;
}

.det-report.show {
    display: block;
}

.det-header {
    background: var(--dkgreen);
    padding: 24px 26px;
    display: flex;
    align-items: center;
    gap: 14px;
}

.det-photo {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    color: white;
    flex-shrink: 0;
    overflow: hidden;
}

.det-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.det-title {
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    color: white;
    font-weight: 700;
}

.det-sub {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 3px;
}

.det-badge {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 14px;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;
}

.det-body {
    padding: 24px 26px;
}

.det-section {
    margin-bottom: 22px;
}

.det-section-title {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.det-section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.det-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 0;
    border-bottom: 1px solid var(--border);
    font-size: 12px;
}

.det-row:last-child {
    border: none;
}

.det-key {
    color: var(--muted);
}

.det-val {
    font-weight: 500;
    color: var(--text);
}

.det-status {
    font-size: 10px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 20px;
}

.det-status.verified {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.not-verified {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.3);
}

.det-status.forged {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-status.clear {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.record {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-status.positive {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.negative {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-warning {
    background: var(--red-lt);
    border: 1px solid rgba(231, 76, 60, 0.3);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 11px;
    color: #7F1D1D;
    margin-top: 12px;
}

/* ── CARE ID ── */
.cid-cat {
    font-size: 8px;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 20px;
}

.cid-cat-dark {
    color: rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
}

.cid-cat-light {
    color: var(--dkgreen);
    background: var(--ltgreen);
    border: 1px solid var(--border);
}

.cid-share-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 9px 10px;
    border-radius: 9px;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    transition: 0.18s;
}

.cid-share-btn.wa {
    background: #25D366;
    color: white;
}

.cid-share-btn.wa:hover {
    background: #1DA851;
}

.cid-share-btn.copy {
    background: white;
    color: var(--dkgreen);
    border: 1px solid var(--border);
}

.cid-share-btn.copy:hover {
    background: var(--ltgreen);
}

/* ── UTILITIES ── */
@keyframes fadeUp {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.notice {
    padding: 14px 18px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.6;
    margin-bottom: 16px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.notice.info {
    background: rgba(37, 99, 235, 0.06);
    border: 1px solid rgba(37, 99, 235, 0.15);
    color: #1D4ED8;
}

.notice.warn {
    background: #FFFBEB;
    border: 1px solid rgba(255, 151, 0, 0.3);
    color: #92400E;
}

.notice.danger {
    background: var(--red-lt);
    border: 1px solid rgba(231, 76, 60, 0.3);
    color: #7F1D1D;
}
</style>